/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.
*/


import {
	animate,
	state,
	style,
	transition,
	trigger,
} from "@angular/animations";
import { CommonModule } from "@angular/common";
import {
	Component,
	ElementRef,
	HostListener,
	inject,
	Injector,
	input,
	type OnDestroy,
	type OnInit,
	output,
	viewChild,
	ViewContainerRef
} from "@angular/core";
import { ThemeService } from "@app/@services/generic/theme.service";
import { takeUntil } from "rxjs";
import { DialogGeneric } from "../dialog.abstract";
import { BACKDROP_ANIMATIONS } from "../dialog.animations";
import type { CbDialogConfig, CbDialogRef } from "../dialog.class";
import { DialogService } from "../dialog.service";

@Component({
	selector: "cb-fullscreen-dialog",
	templateUrl: "./fullscreen-dialog.component.html",
	styleUrl: "./fullscreen-dialog.component.scss",
	standalone: true,
	imports: [CommonModule],
	animations: [
		...BACKDROP_ANIMATIONS,
		trigger("dialogAnimation", [
			state("opened", style({ opacity: 1 })),
			state("closed", style({ opacity: 0 })),
			transition("opened => closed", [
				animate("80ms ease-out", style({ opacity: 0 })),
			]),
			transition("closed => opened", [
				style({ opacity: 0 }),
				animate("80ms ease-in", style({ opacity: 1 })),
			]),
		]),
	],
})
export class FullscreenDialogComponent
	extends DialogGeneric
	implements OnInit, OnDestroy {
	public override dialogRef = input.required<CbDialogRef>();
	public override dialogConfig = input.required<CbDialogConfig>();

	protected readonly injector: Injector = inject(Injector);
	protected readonly vcr: ViewContainerRef = inject(ViewContainerRef);
	protected readonly themeService: ThemeService = inject(ThemeService);
	protected readonly dialogService: DialogService = inject(DialogService);

	public onDestroy = output<void>();

	/**
	 * The dialog wrapper element.
	 */
	public override dialogWrapper = viewChild.required<ElementRef<any>>("DialogWrapper");

	@HostListener("document:keydown", ["$event"])
	handleKeyboardEvent(event: KeyboardEvent) {
		if (event.key === "Escape") {
			event.preventDefault();
			this.dialogService.escKeyPressed.next();
		}
	}

	ngOnInit(): void {
		this.init();

		this.onDestroy$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.onDestroy.emit();
		});
	}

	ngOnDestroy(): void {
		this.destroy();
	}
}
